import { RepStatusEnum } from "@repo/models";
import { DateTimePicker, DropDownList, FiltersV2, Loading, TextBox, ToggleSlider } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import type { RepsFilter } from "../../api/repsApi";

import * as Queries from "../../api/queries";
import { REPS_SETTINGS, useFilterItemQuery } from "../../api/queries";
import RepsApi from "../../api/repsApi";
import { emptySegmentFilters, SegmentFilters } from "../../components/segmentFilters";
import { RepsGrid } from "./grid";
import {
	ACTIVE_STATUS,
	AGENT_ID,
	BIRTH_DATE,
	DISCIPLINE_STATUS,
	EMAIL_ADDRESS,
	FIRST_NAME,
	IS_TEST,
	LAST_FOUR_SSN,
	LAST_NAME,
	ONBOARDING_END_DATE,
	ONBOARDING_START_DATE,
	PHONE_NUMBER,
	POSITION,
	REP_ID,
	STATUS,
	textPlaceholder,
} from "./strings";

const repMultiStatuses = [
	{ label: "Active", value: 1 },
	{ label: "Onboarding", value: 2 },
	{ label: "Awaiting Activation", value: 3 },
	{ label: "Inactive", value: 4 },
	{ label: "On Hold", value: 5 },
];

export const repStatuses = [
	{ label: "Onboarding", value: RepStatusEnum.Onboarding },
	{ label: "Awaiting Activation", value: RepStatusEnum.AwaitingActivation },
	{ label: "Contingent Start", value: RepStatusEnum.ContingentStart },
	{ label: "Active", value: RepStatusEnum.Active },
	{ label: "Terminated", value: RepStatusEnum.Terminated },
	{ label: "Deactivated", value: RepStatusEnum.Deactivated },
	{ label: "Awaiting Contingent Activation", value: RepStatusEnum.AwaitingContingentActivation },
	{ label: "On Hold", value: RepStatusEnum.OnHold },
	{ label: "Awaiting Contingent Approval", value: RepStatusEnum.AwaitingContingentApproval },
	{ label: "Suspended", value: RepStatusEnum.Suspended },
];

const disciplineStatuses = [
	{ label: "Probation", value: 1 },
	{ label: "Suspended", value: 2 },
	{ label: "Blocklisted", value: 3 },
	{ label: "No Active Discipline", value: 4 },
];

const yesNoAllOptions = [
	{ label: textPlaceholder, value: "ALL" },
	{ label: "Yes", value: "YES" },
	{ label: "No", value: "NO" },
];

const initialFilters: RepsFilter = {
	currentPage: 1,
	itemsPerPage: 25,
	brands: emptySegmentFilters.brandIds,
	channels: emptySegmentFilters.channelIds,
	marketers: emptySegmentFilters.marketerIds,
	states: emptySegmentFilters.stateIds,
	branches: emptySegmentFilters.branchIds,
	position: null,
	activeStatus: [1, 2],
	status: [],
	disciplineStatus: [],
	lastFourSSN: "",
	dateOfBirth: null,
	onboardingStartDate: null,
	onboardingEndDate: null,
	isTest: null,
	agentId: "",
	phoneNumber: "",
	email: "",
	firstName: "",
	lastName: "",
	login: "",
	segments: false,
};

type FilterValueType = string | number | boolean | Date | (string | number)[];

export function RepsPage() {
	const [filters, setFilters] = useState<RepsFilter>(initialFilters);
	const [executedFilters, setExecutedFilters] = useState<RepsFilter>(initialFilters);

	const [repTypes] = useFilterItemQuery([Queries.REPTYPES], () => RepsApi.getRepTypes());

	const settingsQuery = useQuery({
		queryKey: [REPS_SETTINGS],
		queryFn: () => RepsApi.getSettings(),
	});

	const filterChanged = useCallback(
		(value: FilterValueType, name: string) => {
			setFilters((prev) => {
				return { ...prev, [name]: value };
			});
		},
		[setFilters],
	);

	const repTypeValues = useMemo(
		() => repTypes.data?.map((r) => ({ value: r.id, label: r.name })) ?? [],
		[repTypes.data],
	);
	const segmentFiltersChanged = useCallback(
		(s) =>
			setFilters((prev) => ({
				...prev,
				brands: s.brandIds,
				channels: s.channelIds,
				marketers: s.marketerIds,
				states: s.stateIds,
				branches: s.branchIds,
			})),
		[setFilters],
	);

	return (
		<Loading loading={settingsQuery.isLoading} message="Loading settings...">
			<div className="w-full">
				<FiltersV2
					onApply={() =>
						setExecutedFilters((currentFilters) => ({
							...filters,
							currentPage: 1,
							itemsPerPage: currentFilters.itemsPerPage,
						}))
					}
				>
					<ToggleSlider
						id="segments"
						label="Show Segments?"
						value={filters.segments}
						changeHandler={filterChanged}
					/>
					<SegmentFilters onChange={segmentFiltersChanged} />

					<DropDownList
						id={POSITION}
						label={"Position:"}
						values={repTypeValues}
						value={filters.position ?? textPlaceholder}
						placeholder={textPlaceholder}
						changeHandler={filterChanged}
						fullLength
					/>
					<DropDownList
						id={ACTIVE_STATUS}
						label={"Multi Status:"}
						values={repMultiStatuses}
						isMulti
						value={filters.activeStatus}
						placeholder={textPlaceholder}
						changeHandler={filterChanged}
						fullLength
					/>
					<DropDownList
						id={STATUS}
						label={"Status:"}
						values={repStatuses}
						isMulti
						value={filters.status}
						placeholder={textPlaceholder}
						changeHandler={filterChanged}
						fullLength
					/>
					<DropDownList
						id={DISCIPLINE_STATUS}
						label={"Discipline Status:"}
						values={disciplineStatuses}
						isMulti
						value={filters.disciplineStatus}
						placeholder={textPlaceholder}
						changeHandler={filterChanged}
						fullLength
					/>

					<DropDownList
						id={IS_TEST}
						label={"Is Test:"}
						values={yesNoAllOptions}
						value={filters.isTest === null ? "ALL" : filters.isTest ? "YES" : "NO"}
						changeHandler={(v) => {
							const mapped = v === "ALL" ? null : v === "YES";
							filterChanged(mapped, IS_TEST);
						}}
						fullLength
					/>
					<TextBox
						id={LAST_FOUR_SSN}
						label={"Last four digits SSN:"}
						value={filters.lastFourSSN ?? ""}
						changeHandler={filterChanged}
						placeholder={"Contains"}
					/>
					<DateTimePicker
						id={BIRTH_DATE}
						label={"Birth Date:"}
						dateOnly
						value={filters.dateOfBirth}
						changeHandler={filterChanged}
					/>
					<TextBox
						id={PHONE_NUMBER}
						label={"Phone Number:"}
						value={filters.phoneNumber}
						changeHandler={filterChanged}
						placeholder={"Contains"}
					/>
					<TextBox
						id={EMAIL_ADDRESS}
						label={"Email:"}
						value={filters.email}
						changeHandler={filterChanged}
						placeholder={"Contains"}
					/>
					<TextBox
						id={FIRST_NAME}
						label={"First Name:"}
						value={filters.firstName}
						changeHandler={filterChanged}
						placeholder={"Contains"}
					/>
					<TextBox
						id={LAST_NAME}
						label={"Last Name:"}
						value={filters.lastName}
						changeHandler={filterChanged}
						placeholder={"Contains"}
					/>
					<TextBox
						id={REP_ID}
						label={"Rep Id:"}
						value={filters.login}
						changeHandler={filterChanged}
						placeholder={"Contains"}
					/>
					<TextBox
						id={AGENT_ID}
						label={"NRG Agent Id:"}
						value={filters.agentId ?? ""}
						changeHandler={filterChanged}
						placeholder={"Is Exactly"}
					/>
					<DateTimePicker
						id={ONBOARDING_START_DATE}
						label={"Onboarded After:"}
						dateOnly
						value={filters.onboardingStartDate}
						changeHandler={filterChanged}
					/>
					<DateTimePicker
						id={ONBOARDING_END_DATE}
						label={"Onboarded Before:"}
						dateOnly
						value={filters.onboardingEndDate}
						changeHandler={filterChanged}
					/>
				</FiltersV2>
				<RepsGrid filters={executedFilters} setFilters={setExecutedFilters} settings={settingsQuery.data} />
			</div>
		</Loading>
	);
}
